import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Formik, Form, Field, FieldArray } from 'formik';
import { useNavigate, useLocation } from 'react-router-dom';
import Grid from 'apollo-react/components/Grid';
import Button from 'apollo-react/components/Button';
import { toast } from 'react-toastify';
import HcpSignupPageFrame from './HcpSignupPageFrame';
import { FormikTextField, FormikSelect } from '../common/FormikComponent';
import MenuItem from 'apollo-react/components/MenuItem';
import {
  addressCountryOptions,
  licenseFieldConfigDefaults,
  formatHcpFileUploadHint,
  buildHcpSignupWizardAttachmentFileConfig,
  getHcpSignupWizardAttachmentFileValidationOpts,
  hcpSignupFormFieldVariant,
} from './hcpSignupDefaults';
import {
  HCP_SIGNUP_FIELD_HINTS,
  HcpSignupLabelWithHint,
  HcpSignupNativeDateLabel,
} from './HcpSignupFieldLabel';
import { buildLicensesFormSchema } from './licensesSchema';
import {
  getSignupDraft,
  postLicensesDraft,
  HCP_SIGNUP_DRAFT_STORAGE_KEY,
} from './hcpSignupApi';
import {
  shouldRedirectForward,
  deriveResumeSignupPath,
  HCP_SIGNUP_NAVIGATE_BACK_STATE,
} from './hcpSignupFlowUtils';
import { useLicensingExperienceOptional } from '../../contexts/LicensingExperienceContext';
import { useDebouncedValidDraftSave } from './useDebouncedValidDraftSave';
import { normalizeWizardDateInput } from './hcpSignupDateUtils';
import FileFieldInput from '../common/FormCrafterFormRenderer/inputs/fields/FileFieldInput';

function newRowKey() {
  return `k-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
}

function createEmptyLicenseRow() {
  return {
    _rowKey: newRowKey(),
    prefix: '',
    licenseNumber: '',
    issuingCountry: '',
    authorityName: '',
    issueDate: '',
    expiryDate: '',
    attachmentFile: null,
    existingAttachmentUploadId: null,
    attachmentLabel: '',
    licenseRowId: null,
  };
}

/** @param {object[]|undefined} list */
function draftLicensesToRows(list) {
  if (!Array.isArray(list) || list.length === 0)
    return [createEmptyLicenseRow()];
  return list.map((L) => ({
    _rowKey: newRowKey(),
    prefix: L.prefix || '',
    licenseNumber: L.licenseNumber || '',
    issuingCountry: L.issuingCountry || '',
    authorityName: L.authorityName || '',
    issueDate: L.issueDate ? String(L.issueDate).slice(0, 10) : '',
    expiryDate: L.expiryDate ? String(L.expiryDate).slice(0, 10) : '',
    attachmentFile: null,
    existingAttachmentUploadId: L.attachment?.id ?? null,
    attachmentLabel: L.attachment?.originalName
      ? `Uploaded: ${L.attachment.originalName}`
      : '',
    licenseRowId: L.licenseRowId ?? null,
  }));
}

/** Strip legacy "Uploaded:" prefix for FileList display. */
function licenseAttachmentDisplayName(row) {
  const raw = row?.attachmentLabel;
  if (!raw) return '';
  return String(raw)
    .replace(/^Uploaded:\s*/i, '')
    .trim();
}

/** Map API `attachment` field to Formik `attachmentFile` errors. */
function mapServerLicenseErrors(licensesErrors) {
  if (!Array.isArray(licensesErrors)) return licensesErrors;
  return licensesErrors.map((e) => {
    if (!e || typeof e !== 'object') return e;
    const o = { ...e };
    if (o.attachment) {
      o.attachmentFile = o.attachment;
      delete o.attachment;
    }
    return o;
  });
}

function LicensesAutoSaveWatcher({
  draftId,
  mergedLicenseCfg,
  formik,
  setInitialLicenses,
}) {
  const { values, dirty, isSubmitting, validateForm, setErrors } = formik;
  const valuesRef = useRef(values);
  valuesRef.current = values;

  const persistQuiet = useCallback(async () => {
    if (!draftId) return;
    try {
      const res = await postLicensesDraft(
        draftId,
        valuesRef.current.licenses,
        mergedLicenseCfg
      );
      if (!res.success) {
        if (res.errors?.licenses)
          setErrors({ licenses: mapServerLicenseErrors(res.errors.licenses) });
        return;
      }
      setInitialLicenses(draftLicensesToRows(res.data.licenses));
    } catch (e) {
      const payload = e.response?.data;
      if (payload?.errors?.licenses) {
        setErrors({
          licenses: mapServerLicenseErrors(payload.errors.licenses),
        });
      }
    }
  }, [draftId, mergedLicenseCfg, setErrors, setInitialLicenses]);

  useDebouncedValidDraftSave({
    enabled: Boolean(draftId),
    dirty,
    isSubmitting,
    valuesWatch: values,
    validateForm,
    save: persistQuiet,
  });

  return null;
}

/** Next step uses imperative `submitForm()` so Apollo Button reliably triggers Formik. */
function LicensesNextStepButton({ submitForm, isSubmitting }) {
  return (
    <Button
      type="button"
      variant="primary"
      className="loginButton"
      disabled={isSubmitting}
      onClick={() => submitForm()}
    >
      Next step
    </Button>
  );
}

/** @param {{ onNext?: (data: object) => void, licenseFieldConfig?: object }} props */
export default function HcpSignupLicensesStep({ onNext, licenseFieldConfig }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { mode } = useLicensingExperienceOptional();

  useEffect(() => {
    if (mode === 'license_tracking') {
      navigate('/hcp-signup/step-1', { replace: true });
    }
  }, [mode, navigate]);

  const navigateBackChosen = Boolean(location.state?.hcpSignupNavigateBack);
  const mergedLicenseCfg = useMemo(
    () => ({ ...licenseFieldConfigDefaults, ...licenseFieldConfig }),
    [licenseFieldConfig]
  );
  const validationSchema = useMemo(
    () => buildLicensesFormSchema(mergedLicenseCfg),
    [mergedLicenseCfg]
  );

  const licenseAttachmentFileConfig = useMemo(
    () => buildHcpSignupWizardAttachmentFileConfig(1),
    []
  );

  const licenseAttachmentUploadHint = useMemo(() => {
    const o = getHcpSignupWizardAttachmentFileValidationOpts();
    return formatHcpFileUploadHint(
      o.acceptExt,
      o.maxMb,
      mergedLicenseCfg.licenseAttachmentRequired
    );
  }, [mergedLicenseCfg.licenseAttachmentRequired]);

  const [draftId, setDraftId] = useState(null);
  const [initialLicenses, setInitialLicenses] = useState([
    createEmptyLicenseRow(),
  ]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancel = false;
    (async () => {
      const rawId = window.localStorage.getItem(HCP_SIGNUP_DRAFT_STORAGE_KEY);
      const id = rawId ? parseInt(rawId, 10) : NaN;

      if (Number.isNaN(id)) {
        if (!cancel) {
          toast.error('Complete Step 1 first.');
          navigate('/hcp-signup/step-1');
        }
        return;
      }

      try {
        const res = await getSignupDraft(id);
        if (cancel) return;
        if (!res?.success || !res.data?.draftId) {
          toast.error('No sign-up draft found. Complete Step 1 first.');
          navigate('/hcp-signup/step-1');
          return;
        }
        if (shouldRedirectForward(2, res.data, navigateBackChosen)) {
          navigate(deriveResumeSignupPath(res.data), { replace: true });
          return;
        }
        setDraftId(res.data.draftId);
        setInitialLicenses(draftLicensesToRows(res.data.licenses));
      } catch {
        toast.error('Could not load draft.');
        navigate('/hcp-signup/step-1');
      } finally {
        if (!cancel) setReady(true);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [navigate, navigateBackChosen]);

  const handleSubmit = useCallback(
    async (values, { setSubmitting, setErrors }) => {
      if (!draftId) {
        toast.error('Missing draft — go back to Step 1.');
        setSubmitting(false);
        navigate('/hcp-signup/step-1');
        return;
      }

      try {
        const res = await postLicensesDraft(
          draftId,
          values.licenses,
          mergedLicenseCfg
        );

        if (!res.success) {
          if (res.errors?.licenses)
            setErrors({
              licenses: mapServerLicenseErrors(res.errors.licenses),
            });
          toast.error(res.message || 'Could not save');
          setSubmitting(false);
          return;
        }

        toast.success('License draft saved');
        setInitialLicenses(draftLicensesToRows(res.data.licenses));

        if (typeof onNext === 'function') onNext(res.data);
        else navigate('/hcp-signup/step-3');
      } catch (e) {
        const payload = e.response?.data;
        if (payload?.errors?.licenses) {
          setErrors({
            licenses: mapServerLicenseErrors(payload.errors.licenses),
          });
        }
        toast.error(payload?.message || 'Save failed');
      } finally {
        setSubmitting(false);
      }
    },
    [draftId, mergedLicenseCfg, navigate, onNext]
  );

  if (!ready) {
    return (
      <HcpSignupPageFrame>
        <Grid container justifyContent="center" className="signInTitleGrid">
          <p className="signInDesc">Loading…</p>
        </Grid>
      </HcpSignupPageFrame>
    );
  }

  const prefixReq =
    mergedLicenseCfg.prefixRequired === true ||
    mergedLicenseCfg.prefixOptional === false;

  return (
    <HcpSignupPageFrame>
      <Grid container justifyContent="center" className="signInTitleGrid">
        <div>
          <h2 className="signInTitle">HCP sign-up — license information</h2>
          <p className="signInDesc">
            Step 2 of 3. Use &quot;Add more&quot; for additional licenses. Your
            licenses are saved automatically once the form is valid.
          </p>
          <button
            type="button"
            className="authWizardBack"
            onClick={() =>
              navigate('/hcp-signup/step-1', {
                state: HCP_SIGNUP_NAVIGATE_BACK_STATE,
              })
            }
          >
            ← Back to general information
          </button>
        </div>
      </Grid>

      <Grid container className="formContainer">
        <Grid container justifyContent="center">
          <Grid item xs={12}>
            <Formik
              key={draftId || 'lic'}
              initialValues={{ licenses: initialLicenses }}
              enableReinitialize
              validationSchema={validationSchema}
              validateOnChange
              validateOnBlur
              onSubmit={handleSubmit}
            >
              {(formik) => {
                const {
                  values,
                  errors,
                  touched,
                  setFieldValue,
                  submitForm,
                  isSubmitting,
                } = formik;
                return (
                  <Form noValidate>
                    <LicensesAutoSaveWatcher
                      draftId={draftId}
                      mergedLicenseCfg={mergedLicenseCfg}
                      formik={formik}
                      setInitialLicenses={setInitialLicenses}
                    />
                    <FieldArray name="licenses">
                      {(arrayHelpers) => (
                        <>
                          {values.licenses.map((row, index) => {
                            const base = `licenses.${index}`;
                            const rowErrors = errors.licenses?.[index];
                            const rowTouched = touched.licenses?.[index];

                            return (
                              <React.Fragment
                                key={row._rowKey || row.licenseRowId || index}
                              >
                                {index > 0 ? (
                                  <hr className="authFormDivider" />
                                ) : null}
                                <div>
                                  <p className="authQualificationTitle">
                                    License {index + 1}
                                  </p>

                                  <Grid
                                    container
                                    rowSpacing={0}
                                    columnSpacing={2}
                                    className="authWizardFieldsGrid"
                                  >
                                    <Grid item xs={4}>
                                      <FormikTextField
                                        name={`${base}.prefix`}
                                        label={
                                          <HcpSignupLabelWithHint
                                            text={
                                              prefixReq
                                                ? 'Prefix'
                                                : 'Prefix (optional)'
                                            }
                                            hint={
                                              HCP_SIGNUP_FIELD_HINTS.licensePrefix
                                            }
                                          />
                                        }
                                        required={prefixReq}
                                        fullWidth
                                        variant={hcpSignupFormFieldVariant}
                                      />
                                    </Grid>
                                    <Grid item xs={8}>
                                      <FormikTextField
                                        name={`${base}.licenseNumber`}
                                        label={
                                          <HcpSignupLabelWithHint
                                            text="License number"
                                            hint={
                                              HCP_SIGNUP_FIELD_HINTS.licenseNumber
                                            }
                                          />
                                        }
                                        required
                                        fullWidth
                                        variant={hcpSignupFormFieldVariant}
                                      />
                                    </Grid>

                                    <Grid item xs={6}>
                                      <FormikSelect
                                        name={`${base}.issuingCountry`}
                                        label={
                                          <HcpSignupLabelWithHint
                                            text="Issuing country"
                                            hint={
                                              HCP_SIGNUP_FIELD_HINTS.issuingCountry
                                            }
                                          />
                                        }
                                        fullWidth
                                        required
                                        variant={hcpSignupFormFieldVariant}
                                      >
                                        <MenuItem value="" disabled>
                                          <em>Select country</em>
                                        </MenuItem>
                                        {addressCountryOptions.map((o) => (
                                          <MenuItem
                                            key={o.value}
                                            value={o.value}
                                          >
                                            {o.label}
                                          </MenuItem>
                                        ))}
                                      </FormikSelect>
                                    </Grid>
                                    <Grid item xs={6}>
                                      <FormikTextField
                                        name={`${base}.authorityName`}
                                        label={
                                          <HcpSignupLabelWithHint
                                            text="Authority name"
                                            hint={
                                              HCP_SIGNUP_FIELD_HINTS.licenseAuthorityName
                                            }
                                          />
                                        }
                                        required
                                        fullWidth
                                        variant={hcpSignupFormFieldVariant}
                                      />
                                    </Grid>

                                    <Grid item xs={6}>
                                      <Field name={`${base}.issueDate`}>
                                        {({ field, meta, form: f }) => (
                                          <div className="authNativeDateWrap">
                                            <HcpSignupNativeDateLabel
                                              htmlFor={`${base}-issue`}
                                              text="Issue date"
                                              hint={
                                                HCP_SIGNUP_FIELD_HINTS.licenseIssueDate
                                              }
                                              required
                                            />
                                            <input
                                              id={`${base}-issue`}
                                              type="date"
                                              className="authNativeDateInput"
                                              name={field.name}
                                              value={field.value}
                                              onBlur={field.onBlur}
                                              onChange={(e) => {
                                                const iso =
                                                  normalizeWizardDateInput(
                                                    e.target.value
                                                  );
                                                f.setFieldValue(
                                                  field.name,
                                                  iso
                                                );
                                              }}
                                            />
                                            {meta.touched && meta.error ? (
                                              <span className="authFieldError">
                                                {meta.error}
                                              </span>
                                            ) : null}
                                          </div>
                                        )}
                                      </Field>
                                    </Grid>
                                    <Grid item xs={6}>
                                      <Field name={`${base}.expiryDate`}>
                                        {({ field, meta, form: f }) => (
                                          <div className="authNativeDateWrap">
                                            <HcpSignupNativeDateLabel
                                              htmlFor={`${base}-exp`}
                                              text="Expiry date"
                                              hint={
                                                HCP_SIGNUP_FIELD_HINTS.licenseExpiryDate
                                              }
                                              required
                                            />
                                            <input
                                              id={`${base}-exp`}
                                              type="date"
                                              className="authNativeDateInput"
                                              name={field.name}
                                              value={field.value}
                                              onBlur={field.onBlur}
                                              onChange={(e) => {
                                                const iso =
                                                  normalizeWizardDateInput(
                                                    e.target.value
                                                  );
                                                f.setFieldValue(
                                                  field.name,
                                                  iso
                                                );
                                              }}
                                            />
                                            {meta.touched && meta.error ? (
                                              <span className="authFieldError">
                                                {meta.error}
                                              </span>
                                            ) : null}
                                          </div>
                                        )}
                                      </Field>
                                    </Grid>

                                    <Grid item xs={12}>
                                      <FileFieldInput
                                        id={`hcp_signup_license_attachment_${row._rowKey}`}
                                        label={
                                          <HcpSignupLabelWithHint
                                            text="License attachment"
                                            hint={
                                              HCP_SIGNUP_FIELD_HINTS.licenseAttachment
                                            }
                                            showRequiredAfterText={
                                              mergedLicenseCfg.licenseAttachmentRequired
                                            }
                                          />
                                        }
                                        columnStyle={{
                                          width: '100%',
                                          maxWidth: '100%',
                                        }}
                                        value={
                                          row.attachmentFile instanceof File
                                            ? [row.attachmentFile]
                                            : []
                                        }
                                        onUpload={(uploaded) => {
                                          const list = Array.isArray(uploaded)
                                            ? uploaded
                                            : [uploaded];
                                          const next = list.find(
                                            (x) => x instanceof File
                                          );
                                          if (!next) return;
                                          setFieldValue(
                                            `${base}.attachmentFile`,
                                            next
                                          );
                                          setFieldValue(
                                            `${base}.existingAttachmentUploadId`,
                                            null
                                          );
                                          setFieldValue(
                                            `${base}.attachmentLabel`,
                                            next.name
                                          );
                                        }}
                                        onDelete={() => {
                                          setFieldValue(
                                            `${base}.attachmentFile`,
                                            null
                                          );
                                          setFieldValue(
                                            `${base}.attachmentLabel`,
                                            ''
                                          );
                                        }}
                                        onServerFileDelete={async () => {
                                          setFieldValue(
                                            `${base}.existingAttachmentUploadId`,
                                            null
                                          );
                                          setFieldValue(
                                            `${base}.attachmentLabel`,
                                            ''
                                          );
                                        }}
                                        helperText={
                                          rowTouched?.attachmentFile &&
                                          rowErrors?.attachmentFile
                                            ? rowErrors.attachmentFile
                                            : licenseAttachmentUploadHint
                                        }
                                        showError={Boolean(
                                          rowTouched?.attachmentFile &&
                                          rowErrors?.attachmentFile
                                        )}
                                        disabled={false}
                                        required={false}
                                        fileConfig={licenseAttachmentFileConfig}
                                        existingFiles={
                                          row.existingAttachmentUploadId &&
                                          !(row.attachmentFile instanceof File)
                                            ? [
                                                {
                                                  id: row.existingAttachmentUploadId,
                                                  name:
                                                    licenseAttachmentDisplayName(
                                                      row
                                                    ) ||
                                                    `Document #${row.existingAttachmentUploadId}`,
                                                  mimeType: '',
                                                  size: 0,
                                                },
                                              ]
                                            : []
                                        }
                                        eagerUploadEnabled={false}
                                        enablePendingDelete={false}
                                        initialStagedFiles={[]}
                                      />
                                    </Grid>

                                    <Grid
                                      item
                                      xs={12}
                                      className="authFormRowActions"
                                    >
                                      <Button
                                        type="button"
                                        variant="secondary"
                                        className="authWizardSecondaryButton"
                                        disabled={values.licenses.length <= 1}
                                        onClick={() =>
                                          arrayHelpers.remove(index)
                                        }
                                      >
                                        Remove license
                                      </Button>
                                    </Grid>
                                  </Grid>
                                </div>
                              </React.Fragment>
                            );
                          })}

                          <Button
                            type="button"
                            variant="secondary"
                            className="authWizardSecondaryButton"
                            onClick={() =>
                              arrayHelpers.push(createEmptyLicenseRow())
                            }
                          >
                            Add more
                          </Button>
                        </>
                      )}
                    </FieldArray>

                    <LicensesNextStepButton
                      submitForm={submitForm}
                      isSubmitting={isSubmitting}
                    />
                  </Form>
                );
              }}
            </Formik>
          </Grid>
        </Grid>
      </Grid>

      <Grid container justifyContent="center">
        <p className="signUpText">
          Already have an account?{' '}
          <a href="/login" className="signUpLink">
            Sign In
          </a>
        </p>
      </Grid>
    </HcpSignupPageFrame>
  );
}
